import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Send, ShieldCheck, FileCheck, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { homePathForRole } from '../utils/roleHome';
import { authApi } from '../api/client';
import './AuthPage.css';

export default function ForgotPasswordPage() {
  const { t } = useLanguage();
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      navigate(homePathForRole(user.role) || '/', { replace: true });
    }
  }, [loading, isAuthenticated, user, navigate]);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setDone(null);
    if (!email.trim()) {
      setError(t.auth.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    try {
      const res = await authApi.forgotPassword(email);
      setDone(res);
    } catch (err) {
      setError(t.auth[err.data?.error] || t.auth.invalidCredentials);
    }
  };

  const perks = [
    { icon: ShieldCheck, text: t.auth.perkSecure },
    { icon: FileCheck, text: t.auth.perkOrders },
    { icon: CheckCircle, text: t.auth.perkDocs },
  ];

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <aside className="auth-brand-panel">
          <div className="auth-brand-glow" aria-hidden="true" />
          <div className="auth-brand-content">
            <p className="auth-brand-name">
              Tarjemlek
              <span lang="ar">ترجملك</span>
            </p>
            <h1>{t.auth.forgotTitle}</h1>
            <p className="auth-brand-text">{t.auth.forgotHint}</p>
            <ul className="auth-perks">
              {perks.map(({ icon: Icon, text }) => (
                <li key={text}>
                  <Icon size={18} aria-hidden="true" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="auth-form-panel">
          <form className="auth-card" onSubmit={handleSubmit} noValidate>
            <div className="auth-card-head">
              <h2>{t.auth.forgotTitle}</h2>
              <p>{t.auth.forgotHint}</p>
            </div>

            {error && <div className="auth-error" role="alert">{error}</div>}
            {done && (
              <div className="auth-success" role="status">
                <p>{t.auth.forgotSent}</p>
              </div>
            )}

            <div className="auth-field">
              <label htmlFor="forgot-email">{t.auth.email}</label>
              <div className="auth-input">
                <Mail size={17} aria-hidden="true" />
                <input
                  id="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemple.com"
                />
              </div>
            </div>

            <button type="submit" className="btn btn-primary auth-submit">
              <Send size={17} />
              {t.auth.forgotButton}
            </button>

            <p className="auth-switch">
              <Link to="/login">{t.auth.loginLink}</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
